/**
 * AI 标记查询队列
 *
 * 后端 /work/getFileAiTag 接口的 fileIds 支持逗号拼接批量查询，
 * 但组件是逐个渲染的。这里做一层微批合并：同一时间窗内的多个查询
 * 合并成一次请求，避免 N 个素材产生 N 个请求。
 *
 * 不做缓存：上传流程中同一 fileId 重复出现概率极低，缓存收益接近零，
 * 反而可能让用户在别处修改标记后回来看到旧值。
 */

import { postGetFileAiTag } from '../../serve/material';

/** 批量合并的时间窗（ms），同一渲染帧内的查询会被合并 */
const BATCH_WINDOW = 30;

/** 待查询队列：fileId -> [{ resolve, reject }] */
const pendingMap = new Map();

/** 飞行中的请求：fileId -> Promise，用于去重 */
const inflightMap = new Map();

/** 当前批次的定时器 */
let batchTimer = null;

/**
 * 解析接口返回，统一转成 Map<string, number>
 *
 * 后端返回结构：
 * { code: 0, data: [{ file_id: 3850, ai_tag: 1, ai_tag_desc: '全部AI' }] }
 *
 * 这是唯一需要感知后端字段命名的地方，后端结构调整只需改这里。
 *
 * @param {Object} res - 接口响应
 * @returns {Map<string, { aiTag: number, aiTagDesc: string }>}
 */
function parseResponse(res) {
  const result = new Map();
  const list = res && res.data;
  if (!Array.isArray(list)) return result;

  list.forEach(item => {
    if (!item) return;
    const fileId = item.file_id ?? item.fileId;
    if (fileId === null || fileId === undefined || fileId === '') return;
    const aiTag = item.ai_tag ?? item.aiTag;
    // 后端默认值 0（未确认）视同未标记，前端展示为「+AI标记」入口
    const normalizedTag = (aiTag === null || aiTag === undefined || aiTag === '' || aiTag === 0 || aiTag === '0')
      ? null
      : Number(aiTag);
    result.set(String(fileId), {
      aiTag: normalizedTag,
      aiTagDesc: normalizedTag === null ? '' : (item.ai_tag_desc ?? item.aiTagDesc ?? ''),
    });
  });

  return result;
}

/**
 * 执行当前批次的查询
 */
async function flushBatch() {
  batchTimer = null;

  // 取出当前队列并立即清空，避免请求期间新入队的被误清
  const batch = new Map(pendingMap);
  pendingMap.clear();
  if (!batch.size) return;

  const fileIds = Array.from(batch.keys());

  try {
    const res = await postGetFileAiTag({ fileIds: fileIds.join(',') });
    const tagMap = parseResponse(res);

    fileIds.forEach(fileId => {
      // 接口未返回该项即视为未标记
      const hit = tagMap.get(fileId) || { aiTag: null, aiTagDesc: '' };
      batch.get(fileId).forEach(({ resolve }) => resolve(hit));
    });
  } catch (err) {
    // 注意：request 拦截器对业务错误码已做过全局 Message 提示，
    // 这里不再重复弹窗，仅打日志，由调用方决定降级展示
    console.warn('[AiUsageTag] 查询 AI 标记失败', err);
    fileIds.forEach(fileId => {
      batch.get(fileId).forEach(({ reject }) => reject(err));
    });
  } finally {
    fileIds.forEach(fileId => inflightMap.delete(fileId));
  }
}

/**
 * 查询单个文件的 AI 标记（自动合并为批量请求）
 *
 * @param {string|number} fileId - 文件 id
 * @returns {Promise<{ aiTag: number|null, aiTagDesc: string }>}
 */
export function getAiTag(fileId) {
  if (fileId === null || fileId === undefined || fileId === '') {
    return Promise.resolve({ aiTag: null, aiTagDesc: '' });
  }

  const key = String(fileId);

  // 同一 fileId 已在请求中，复用同一个 Promise
  if (inflightMap.has(key)) {
    return inflightMap.get(key);
  }

  const promise = new Promise((resolve, reject) => {
    if (!pendingMap.has(key)) {
      pendingMap.set(key, []);
    }
    pendingMap.get(key).push({ resolve, reject });
  });

  inflightMap.set(key, promise);

  if (!batchTimer) {
    batchTimer = setTimeout(flushBatch, BATCH_WINDOW);
  }

  return promise;
}

/**
 * 批量查询多个文件的 AI 标记
 *
 * @param {Array<string|number>} fileIds - 文件 id 数组
 * @returns {Promise<Map<string, { aiTag: number|null, aiTagDesc: string }>>}
 */
export function getAiTags(fileIds) {
  const list = (fileIds || []).filter(id => id !== null && id !== undefined && id !== '');
  if (!list.length) return Promise.resolve(new Map());

  return Promise.all(
    list.map(id =>
      getAiTag(id)
        .then(res => [String(id), res])
        .catch(() => [String(id), { aiTag: null, aiTagDesc: '' }]),
    ),
  ).then(entries => new Map(entries));
}
